import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { InputRef } from 'antd';
import { useTranslation } from 'react-i18next';
import soundEffects from '@utils/soundEffects';
import {
  parseSerials,
  compilePattern,
  validateBatch,
  type SerialValidationFlags,
  type ValidationFailure,
} from '@utils/serialNumbers';

export interface UseSerialNumberInputProps {
  productFlags: SerialValidationFlags & {
    // 「是否需要序號」由 required_serial_numbers_count > 0 推導，不再有獨立 flag。
    // Hook 內部會以 `?? 0` normalize，count <= 0 等同於不需要序號，submit 全部 no-op。
    required_serial_numbers_count?: number;
  };
  scanQuantity: number;
  maxQuantity?: number;
  existingSerials?: string[];
  initialSerials?: string[];
  onCompleted?: (serials: string[]) => void;
}

export interface UseSerialNumberInputResult {
  input: string;
  setInput: (v: string) => void;
  scannedSerials: string[];
  error: string | null;
  submit: (value?: string) => boolean;
  removeAt: (idx: number) => void;
  replaceSerials: (next: string[]) => void;
  reset: () => void;
  clearError: () => void;
  isCompleted: boolean;
  totalRequired: number;
  inputRef: React.RefObject<InputRef | null>;
}

function errorMessage(
  t: (k: string, opts?: Record<string, unknown>) => string,
  failure: ValidationFailure,
): string {
  switch (failure.reason) {
    case 'pattern_mismatch':
      return t('operator.serial_numbers.pattern_mismatch', { serial: failure.serial });
    case 'duplicate':
      return t('operator.serial_numbers.duplicate', { serial: failure.serial });
    case 'max_reached':
      return t('operator.serial_numbers.max_reached');
    case 'is_product_barcode':
      return t('operator.serial_numbers.is_product_barcode', { serial: failure.serial });
  }
}

export function useSerialNumberInput(props: UseSerialNumberInputProps): UseSerialNumberInputResult {
  const { productFlags, scanQuantity, maxQuantity, existingSerials, initialSerials, onCompleted } = props;
  const { t } = useTranslation();

  const [input, setInput] = useState('');
  const [scannedSerials, setScannedSerials] = useState<string[]>(initialSerials ?? []);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<InputRef | null>(null);

  // Keep a synchronously-updated mirror of scannedSerials so that two calls to
  // submit() inside the same event handler can each see the result of the
  // previous one — React state updates are batched/deferred, so reading
  // scannedSerials from closure during back-to-back synchronous calls would
  // see stale values. The ref is updated inside submit before each setState,
  // and also on every render via useEffect to stay in sync with outside
  // mutations (removeAt, reset, props change).
  const scannedSerialsRef = useRef<string[]>(scannedSerials);
  useEffect(() => {
    scannedSerialsRef.current = scannedSerials;
  });

  // Keep latest onCompleted in a ref so submit's identity doesn't churn when
  // callers inline the callback (which would otherwise break React.memo
  // children that receive submit as a prop).
  const onCompletedRef = useRef(onCompleted);
  useEffect(() => {
    onCompletedRef.current = onCompleted;
  });

  // Mount-time auto-focus for the scanner-in-hand operator (primary user
  // persona). Fires exactly ONCE, never reacts to later state changes — by
  // design:
  //   • Don't refocus on every successful scan: the per-scan re-focus inside
  //     submit() already handles "ready for next scan" (L141/L154 below).
  //   • Don't refocus on isCompleted transitions: if operator deletes one
  //     serial mid-edit (e.g. corrects a typo), stealing focus back would
  //     interrupt whatever field they're editing next.
  //   • Don't refocus when scanQuantity changes: operator might be in the
  //     middle of editing batch / mfg date when qty auto-adjusts.
  //
  // Three silent no-op paths (all expected):
  //   1. inputRef null  — consumer doesn't render the input (e.g.
  //      VerificationFormPanel hides it when required_serial_numbers_count = 0).
  //      Optional chain swallows it.
  //   2. Input is disabled at mount — when scanQuantity=0 (inbound create
  //      mode) totalRequired=0 → isCompleted=true → consumer renders
  //      <Input disabled> → browser silently drops focus(). Same end-state
  //      as before this hook had auto-focus: operator clicks to focus.
  //   3. Consumer mounts hook outside a modal/drawer — 100ms delay is a
  //      pure wait, no animation to clear. Inefficient but harmless.
  //
  // 100ms delay covers antd Modal/Drawer open animations. Focusing before
  // the overlay is fully painted can fail silently on Safari, and antd's
  // own internal focus trap runs near 0ms — 100ms gives us "last writer
  // wins" over both. Empirically stable on antd 5.x; if a future antd
  // upgrade changes timing, set `autoFocus={false}` on the consumer's
  // <Drawer> / <Modal> to take antd out of the race.
  useEffect(() => {
    const id = setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
    return () => clearTimeout(id);
  }, []);

  // 同步 initialSerials prop 變動。Caller（e.g. Inbound FormPage 切 ReceiptItem）
  // 換 row 時會傳入新的 initialSerials，hook 必須把 scannedSerials 重置；不然舊掃描
  // 列表會綁到錯誤 item，造成資料錯誤（PR #1040 review 列為 🔴 blocking）。
  //
  // 用 ref 記住上次「值層級」的快照做比對：避免 caller 每次 render 重新派生
  // initialSerials array（referential inequality 但 deeply equal）時誤把使用者
  // 已掃但還沒 commit 的內容洗掉。第一次 render 已由 useState 取用 initialSerials，
  // 所以這個 effect 從第二次比對起才會 setState。
  const lastSyncedInitialRef = useRef<string[] | undefined>(initialSerials);
  useEffect(() => {
    const prev = lastSyncedInitialRef.current;
    const next = initialSerials;
    const sameLength = (prev?.length ?? 0) === (next?.length ?? 0);
    const deeplyEqual =
      sameLength && (prev ?? []).every((v, i) => v === (next ?? [])[i]);
    if (deeplyEqual) return;
    lastSyncedInitialRef.current = next;
    const fresh = next ?? [];
    scannedSerialsRef.current = fresh;
    setScannedSerials(fresh);
    setInput('');
    setError(null);
  }, [initialSerials]);

  const compiledPattern = useMemo(
    () => compilePattern(productFlags.serial_number_pattern),
    [productFlags.serial_number_pattern],
  );

  // Invariant guard：serial_number_pattern 是壞 regex（compilePattern 回 invalid=true）
  // 時鎖死 isCompleted，避免任意字串通過驗收。用 console.error 留痕（Sentry / 瀏覽器 console 可見），
  // 不偷偷 fallback。
  const required = productFlags.required_serial_numbers_count ?? 0;
  const patternInvalid = compiledPattern.invalid;
  const invalidSerialConfig = patternInvalid;
  if (invalidSerialConfig) {
    // eslint-disable-next-line no-console
    console.error(
      `[useSerialNumberInput] invariant violation: serial_number_pattern is not a parseable RegExp (got ${JSON.stringify(productFlags.serial_number_pattern)})`,
      productFlags,
    );
  }
  const effectiveRequired = invalidSerialConfig ? 0 : required;
  const totalRequired = scanQuantity * effectiveRequired;
  const upperBound = (maxQuantity ?? scanQuantity) * effectiveRequired;

  const isCompleted = !invalidSerialConfig && scannedSerials.length === totalRequired;

  const submit = useCallback(
    (override?: string): boolean => {
      // Invariant 守衛同樣作用在 submit 路徑：壞 pattern / 缺 required 時連嘗試都不要進，
      // 避免 buzzer / soundEffects / 錯誤訊息流出 UI 模糊化 invariant violation 訊號。
      if (invalidSerialConfig) return false;
      const raw = (override ?? input).trim();
      if (raw.length === 0) return false;

      // Global product-barcode short-circuit: silent swallow
      if (productFlags.product_barcode && raw === productFlags.product_barcode) {
        if (override === undefined) setInput('');
        inputRef.current?.focus();
        return false;
      }

      const parsed = parseSerials(raw, productFlags.box_serial_number_delimiter);
      if (parsed.length === 0) return false;

      // Filter out product-barcode parts silently (delimiter case)
      const filtered = productFlags.product_barcode
        ? parsed.filter((s) => s !== productFlags.product_barcode)
        : parsed;
      if (filtered.length === 0) {
        if (override === undefined) setInput('');
        inputRef.current?.focus();
        return false;
      }

      // Read freshest serial list from the ref, not from closure. This is
      // what makes synchronous back-to-back submit() calls correct: the ref
      // is mutated below before state has had a chance to flush.
      const current = scannedSerialsRef.current;
      const existing = new Set<string>([...(existingSerials ?? []), ...current]);
      const remainingSlots = upperBound - current.length;

      const { accepted, failure } = validateBatch(filtered, {
        pattern: compiledPattern.regex,
        existing,
        remainingSlots,
        productBarcode: null,
      });

      if (failure) {
        soundEffects.playBuzzerError();
        setError(errorMessage(t, failure));
        if (override === undefined) setInput('');
        inputRef.current?.focus();
        return false;
      }

      const next = [...current, ...accepted];
      // Mutate the ref synchronously so the next submit() in the same tick
      // sees the new list.
      scannedSerialsRef.current = next;
      setScannedSerials(next);

      setError(null);
      if (override === undefined) setInput('');
      soundEffects.playScan();
      inputRef.current?.focus();

      // Edge-cross detection: only fire onCompleted when crossing from
      // incomplete → complete. Uses current (pre-write) and next (post-write).
      if (current.length < totalRequired && next.length === totalRequired) {
        onCompletedRef.current?.(next);
      }
      return true;
    },
    [
      input,
      productFlags.product_barcode,
      productFlags.box_serial_number_delimiter,
      productFlags.serial_number_pattern,
      existingSerials,
      upperBound,
      compiledPattern.regex,
      invalidSerialConfig,
      t,
      totalRequired,
    ],
  );

  const removeAt = useCallback((idx: number) => {
    // Keep the ref in sync so a synchronous submit() call after removeAt sees
    // the updated list rather than the pre-remove one.
    const next = scannedSerialsRef.current.filter((_, i) => i !== idx);
    scannedSerialsRef.current = next;
    setScannedSerials(next);
  }, []);

  // Escape hatch for external callers (e.g. parent prop sync) that need to
  // wholesale replace the scanned list. Updates the ref synchronously so a
  // subsequent submit() in the same tick sees the new list (e.g. dedup
  // checks against freshly-hydrated serials).
  const replaceSerials = useCallback((next: string[]) => {
    scannedSerialsRef.current = next;
    setScannedSerials(next);
  }, []);

  const reset = useCallback(() => {
    const fresh = initialSerials ?? [];
    scannedSerialsRef.current = fresh;
    setScannedSerials(fresh);
    setInput('');
    setError(null);
  }, [initialSerials]);

  const clearError = useCallback(() => setError(null), []);

  return {
    input,
    setInput,
    scannedSerials,
    error,
    submit,
    removeAt,
    replaceSerials,
    reset,
    clearError,
    isCompleted,
    totalRequired,
    inputRef,
  };
}
